import {
  GridActionsCellItem,
  GridRowParams,
} from '@mui/x-data-grid';
import {
  Paper,
  IconButton,
  Grid,
  Tooltip,
  Tab,
  Tabs,
  TextField,
  Autocomplete,
  Button,
  Alert,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Sync,
  ForwardToInbox,
} from '@mui/icons-material';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { useSnackbar } from 'notistack';
import dayjs from 'dayjs';
import userService from '../../../../services/authority/userService';
import DataList from '../../../../components/DataList';
import accountService from '../../../../services/accountService';
import useConfirmDeleteDialog from '../../../../hooks/useConfirmDeleteDialog';
import invitationService from '../../../invitations/invitationService';
import roleCatalogService from '../../../roleCatalog/roleCatalogService';
import tenantService from '../../../tenants/tenantService';

type UsersTab = 'members' | 'invitations';

const UsersPage = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<UsersTab>('members');
  const [rows, setRows] = useState<any[]>([]);
  const [inviteRows, setInviteRows] = useState<any[]>([]);
  const [roleTemplates, setRoleTemplates] = useState<any[]>([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [roleTemplateId, setRoleTemplateId] = useState<string | null>(null);
  const [createdCode, setCreatedCode] = useState<string | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch<any>();
  const { ConfirmDeleteDialog, setParams } = useConfirmDeleteDialog();
  const activeTenantId = useSelector(
    (state: any) => state.user.userInfo?.activeTenantId
  );

  const loadUsers = () => {
    if (!activeTenantId) {
      setRows([]);
      return;
    }
    dispatch({ type: 'SHOW_QUERY' });
    tenantService
      .listMembers(activeTenantId)
      .then((response) => {
        if (!response.hasError) {
          setRows(
            (response.records ?? []).map((x: any, index: number) => {
              const roles = x.roleTemplateNames ?? x.RoleTemplateNames ?? [];
              const roleList = Array.isArray(roles) ? roles : [];
              return {
                ...x,
                id: x.membershipId ?? x.MembershipId ?? index,
                membershipId: x.membershipId ?? x.MembershipId,
                userId: x.userId ?? x.UserId,
                fullName: x.fullName ?? x.FullName ?? '',
                email: x.email ?? x.Email ?? '',
                status: x.status ?? x.Status ?? '',
                roleTemplateNames: roleList.join(', ') || '—',
                roleTemplateIds:
                  x.roleTemplateIds ?? x.RoleTemplateIds ?? [],
              };
            })
          );
        } else {
          enqueueSnackbar((response.errorMessages ?? []).join(','), {
            variant: 'error',
          });
        }
      })
      .finally(() => dispatch({ type: 'HIDE' }));
  };

  const loadInvitations = () => {
    if (!activeTenantId) {
      setInviteRows([]);
      return;
    }
    dispatch({ type: 'SHOW_QUERY' });
    invitationService
      .list(activeTenantId)
      .then((response) => {
        if (!response.hasError) setInviteRows(response.records ?? []);
        else
          enqueueSnackbar((response.errorMessages ?? []).join(','), {
            variant: 'error',
          });
      })
      .finally(() => dispatch({ type: 'HIDE' }));
  };

  const loadRoleTemplates = () => {
    roleCatalogService.listEnabled().then((response) => {
      if (response.hasError) return;
      const enabled = response.records ?? [];
      const fromEnabled = enabled
        .map((x: any) => x.roleTemplate ?? x.RoleTemplate)
        .filter(Boolean);
      if (fromEnabled.length > 0) {
        setRoleTemplates(fromEnabled);
        return;
      }
      roleCatalogService.listTemplates().then((r) => {
        if (!r.hasError) setRoleTemplates(r.records ?? []);
      });
    });
  };

  useEffect(() => {
    loadUsers();
    loadRoleTemplates();
  }, [activeTenantId]);

  useEffect(() => {
    if (tab === 'invitations') {
      loadInvitations();
    }
  }, [tab, activeTenantId]);

  const memberColumns: any[] = [
    {
      field: 'fullName',
      headerName: 'Megjelenítési név',
      flex: 100,
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 100,
    },
    {
      field: 'roleTemplateNames',
      headerName: 'Szerepkör',
      flex: 120,
    },
    {
      field: 'status',
      headerName: 'Státusz',
      flex: 70,
    },
    {
      field: 'actions',
      type: 'actions',
      flex: 20,
      getActions: (params: GridRowParams) => [
        <GridActionsCellItem
          icon={
            <Tooltip title="Szerkesztés">
              <EditIcon />
            </Tooltip>
          }
          label="Edit"
          color="primary"
          onClick={() =>
            navigate(
              `/authority/users/membership/${params.row.membershipId}`
            )
          }
        />,
        <GridActionsCellItem
          icon={
            <Tooltip title="Törlés">
              <DeleteIcon />
            </Tooltip>
          }
          label="Delete"
          color="primary"
          onClick={() =>
            setParams({
              open: true,
              name: '',
              onConfirm: async () =>
                handleDelete(params.row.userId ?? params.row.id),
            })
          }
        />,
      ],
    } as any,
  ];

  const templateNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of roleTemplates) {
      if (t?.id) map.set(String(t.id), t.name ?? String(t.id));
    }
    return map;
  }, [roleTemplates]);

  const inviteColumns: any[] = [
    { field: 'email', headerName: 'Email', flex: 120 },
    {
      field: 'targetRoleTemplateId',
      headerName: 'Szerepkör',
      flex: 100,
      valueGetter: (_value: any, row: any) =>
        templateNameById.get(String(row.targetRoleTemplateId ?? '')) ??
        row.targetRoleTemplateId ??
        '—',
    },
    {
      field: 'expiresAt',
      headerName: 'Lejárat',
      flex: 100,
      valueGetter: (_value: any, row: any) =>
        row.expiresAt ? dayjs(row.expiresAt).format('YYYY-MM-DD HH:mm') : '—',
    },
    {
      field: 'acceptedAt',
      headerName: 'Elfogadva',
      flex: 100,
      valueGetter: (_value: any, row: any) =>
        row.acceptedAt
          ? dayjs(row.acceptedAt).format('YYYY-MM-DD HH:mm')
          : 'Függőben',
    },
    { field: 'code', headerName: 'Kód', flex: 80 },
  ];

  const handleDelete = (id: number) => {
    userService.delete(id).then((response) => {
      if (response.hasError) {
        enqueueSnackbar(response.errorMessages.join(','), {
          variant: 'error',
        });
      } else {
        enqueueSnackbar('Sikeres törlés', {
          variant: 'success',
        });
        setRows(rows.filter((row) => (row.userId ?? row.id) !== id));
      }
    });
  };

  const createTenantInvitation = () => {
    if (!activeTenantId || !inviteEmail) return;
    dispatch({ type: 'SHOW_SAVE' });
    invitationService
      .create({
        email: inviteEmail,
        tenantId: activeTenantId,
        targetRoleTemplateId: roleTemplateId,
      })
      .then((response) => {
        if (response.hasError) {
          enqueueSnackbar((response.errorMessages ?? []).join(','), {
            variant: 'error',
          });
          return;
        }
        const code = response.result?.code ?? response.result?.Code;
        setCreatedCode(code ?? null);
        enqueueSnackbar('Meghívó létrehozva', { variant: 'success' });
        setInviteEmail('');
        setRoleTemplateId(null);
        loadInvitations();
      })
      .finally(() => dispatch({ type: 'HIDE' }));
  };

  return (
    <Paper>
      <Grid container sx={{ p: 3 }} spacing={2}>
        <Grid size={12}>
          <h2>Felhasználók</h2>
        </Grid>
        <Grid size={12}>
          <Alert severity="info">
            Tagok, szerepkörök és meghívók kezelése.
          </Alert>
        </Grid>
        <Grid size={12}>
          <Tabs
            value={tab}
            onChange={(_e, value: UsersTab) => setTab(value)}
            sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}
          >
            <Tab value="members" label="Tagok" />
            <Tab value="invitations" label="Meghívók" />
          </Tabs>
        </Grid>

        {tab === 'members' && (
          <>
            {!activeTenantId && (
              <Grid size={12}>
                <Alert severity="warning">
                  Nincs kiválasztott szervezet — a tagok listázásához válassz a
                  fejlécben.
                </Alert>
              </Grid>
            )}
            <Grid container size={12} sx={{ justifyContent: 'end', gap: 1 }}>
              <Tooltip title="AAD felhasználók szinkronizálása">
                <IconButton
                  onClick={() => {
                    accountService
                      .syncUsers()
                      .then(() => {
                        enqueueSnackbar('Sikeres szinkronizáció!', {
                          variant: 'success',
                        });
                        setTimeout(() => loadUsers(), 2000);
                      })
                      .catch(() => {
                        enqueueSnackbar('Sikertelen szinkronizáció!', {
                          variant: 'error',
                        });
                      });
                  }}
                  color="primary"
                >
                  <Sync />
                </IconButton>
              </Tooltip>
            </Grid>
            <Grid size={12}>
              <DataList rows={rows} columns={memberColumns} />
            </Grid>
          </>
        )}

        {tab === 'invitations' && (
          <>
            {!activeTenantId && (
              <Grid size={12}>
                <Alert severity="warning">
                  Nincs kiválasztott szervezet — a meghívók listázásához válassz
                  a fejlécben.
                </Alert>
              </Grid>
            )}
            {createdCode && (
              <Grid size={12}>
                <Alert severity="info">
                  Meghívó kód: <strong>{createdCode}</strong>
                </Alert>
              </Grid>
            )}
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                type="email"
                label="Email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                disabled={!activeTenantId}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Autocomplete
                options={roleTemplates}
                getOptionLabel={(o: any) => o.name ?? ''}
                value={
                  roleTemplates.find((t) => t.id === roleTemplateId) ?? null
                }
                onChange={(_e, v: any) => setRoleTemplateId(v?.id ?? null)}
                disabled={!activeTenantId}
                renderInput={(params) => (
                  <TextField {...params} label="Szerepkör" />
                )}
              />
            </Grid>
            <Grid
              size={{ xs: 12, md: 4 }}
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <Button
                variant="contained"
                startIcon={<ForwardToInbox />}
                disabled={!activeTenantId || !inviteEmail}
                onClick={createTenantInvitation}
              >
                Meghívó küldése
              </Button>
            </Grid>
            <Grid size={12}>
              <DataList rows={inviteRows} columns={inviteColumns} />
            </Grid>
          </>
        )}

        <ConfirmDeleteDialog />
      </Grid>
    </Paper>
  );
};

export default UsersPage;
